import clsx from 'clsx';
import { useMemo, useState } from 'react';
import {
  useActivityTypes,
  useCreateLead,
  useCustomFields,
  useMembers,
  useStages,
  useTags,
} from '../../api/queries';
import { api } from '../../api/client';
import type { CustomField, Lead } from '@figureit/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '../../hooks/useViewport';
import {
  contactPickerSupported,
  pickContact,
  useDictation,
} from '../../hooks/useSpeech';
import { today } from '../../utils/format';
import { NEXT_STEP_CHIPS, addDays, appendDictation } from '../../utils/quickLog';
import { Modal } from '../ui/Modal';

/**
 * New-lead form. On phones it opens in "quick capture" mode: just the fields
 * a rep has in hand after a conversation (company, contact, phone, a first
 * note, the next step), with everything else behind "More fields". Desktop
 * shows the full form as before.
 */
export function AddLeadModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /** Called after a successful save; `open` is true when the rep chose "Save & open". */
  onCreated?: (lead: Lead, open: boolean) => void;
}) {
  const stages = useStages().data?.items ?? [];
  const cfs = useCustomFields().data?.items ?? [];
  const tags = useTags().data?.items ?? [];
  const members = useMembers().data?.items ?? [];
  const types = useActivityTypes().data?.items ?? [];
  const create = useCreateLead();
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  const sortedStages = useMemo(() => stages.slice().sort((a, b) => a.order - b.order), [stages]);

  const [showAll, setShowAll] = useState(!isMobile);
  const [saving, setSaving] = useState<null | 'close' | 'open'>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    company: '',
    contactName: '',
    contactTitle: '',
    email: '',
    phone: '',
    source: '',
    stageId: sortedStages[0]?.id ?? '',
    nreValue: '',
    dealValueYearly: '',
    priority: 'B' as 'A' | 'B' | 'C',
    ownerUserId: '',
    nextAction: '',
    nextActionDate: '',
    notes: '',
    firstNote: '',
    tagIds: [] as string[],
    customFields: {} as Record<string, string | number | boolean | null>,
  });

  // Default the stage once data arrives.
  useMemo(() => {
    if (!form.stageId && sortedStages[0]) {
      setForm((f) => ({ ...f, stageId: sortedStages[0]!.id }));
    }
  }, [sortedStages, form.stageId]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const dictation = useDictation((text) =>
    setForm((f) => ({ ...f, firstNote: appendDictation(f.firstNote, text) })),
  );

  const fromContacts = async () => {
    const c = await pickContact();
    if (!c) return;
    setForm((f) => ({
      ...f,
      contactName: c.name ?? f.contactName,
      phone: c.phone ?? f.phone,
      email: c.email ?? f.email,
    }));
  };

  const submit = async (open: boolean) => {
    if (!form.company.trim()) {
      setError('Company is required');
      return;
    }
    if (!form.stageId) {
      setError('Please pick a stage');
      return;
    }
    setError(null);
    setSaving(open ? 'open' : 'close');
    try {
      const lead = await create.mutateAsync({
        company: form.company.trim(),
        contactName: form.contactName || null,
        contactTitle: form.contactTitle || null,
        email: form.email || null,
        phone: form.phone || null,
        source: form.source || null,
        stageId: form.stageId,
        nreValue: form.nreValue === '' ? null : Number(form.nreValue),
        dealValueYearly: form.dealValueYearly === '' ? null : Number(form.dealValueYearly),
        priority: form.priority,
        ownerUserId: form.ownerUserId || null,
        nextAction: form.nextAction || (form.nextActionDate ? 'Follow up' : null),
        nextActionDate: form.nextActionDate || null,
        notes: form.notes || null,
        tagIds: form.tagIds,
        customFields: form.customFields,
      });
      // The first note becomes the first timeline entry (and stamps Last Touch),
      // rather than being buried in the static Notes field.
      if (form.firstNote.trim()) {
        const noteType = types.find((t) => t.name.toLowerCase() === 'note');
        await api(`/api/leads/${lead.id}/activities`, {
          method: 'POST',
          body: JSON.stringify({
            typeId: noteType?.id,
            typeName: noteType ? undefined : 'Note',
            date: today(),
            summary: form.firstNote.trim(),
          }),
        });
        qc.invalidateQueries({ queryKey: ['activities', lead.id] });
        qc.invalidateQueries({ queryKey: ['leads'] });
      }
      onCreated?.(lead, open);
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Could not save the lead.');
      setSaving(null);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="New Lead"
      footer={
        <>
          {/* Phones get × in the header, so Cancel only shows on wider screens. */}
          <button className="btn btn-secondary hidden sm:inline-flex" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-secondary flex-1 sm:flex-none whitespace-nowrap"
            onClick={() => submit(true)}
            disabled={!!saving}
          >
            {saving === 'open' ? 'Saving…' : 'Save & open'}
          </button>
          <button
            className="btn btn-primary flex-1 sm:flex-none"
            onClick={() => submit(false)}
            disabled={!!saving}
          >
            {saving === 'close' ? 'Saving…' : 'Add lead'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Company *" htmlFor="nl-company">
          <input
            id="nl-company"
            value={form.company}
            onChange={(e) => set('company', e.target.value)}
            autoFocus={!isMobile}
            autoCapitalize="words"
            enterKeyHint="next"
          />
        </Field>
        <Field
          label="Contact name"
          htmlFor="nl-contact"
          right={
            contactPickerSupported() && (
              <button type="button" className="text-[12px] text-accent2" onClick={fromContacts}>
                📇 From contacts
              </button>
            )
          }
        >
          <input
            id="nl-contact"
            value={form.contactName}
            onChange={(e) => set('contactName', e.target.value)}
            autoCapitalize="words"
            autoComplete="off"
            enterKeyHint="next"
          />
        </Field>
        <Field label="Phone" htmlFor="nl-phone">
          <input
            id="nl-phone"
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            enterKeyHint="next"
          />
        </Field>
        <Field label="Email" htmlFor="nl-email">
          <input
            id="nl-email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            enterKeyHint="next"
          />
        </Field>
        <Field label="Stage" htmlFor="nl-stage">
          <select id="nl-stage" value={form.stageId} onChange={(e) => set('stageId', e.target.value)}>
            {sortedStages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <div className="flex gap-2">
            {(['A', 'B', 'C'] as const).map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={form.priority === p}
                className={clsx(
                  'flex-1 rounded-md border min-h-[44px] sm:min-h-[36px] text-sm font-semibold',
                  form.priority === p
                    ? 'bg-accent border-accent text-white'
                    : 'bg-surface2 border-border text-text2',
                )}
                onClick={() => set('priority', p)}
              >
                {p}
                <span className="font-normal text-xs ml-1 opacity-80">
                  {p === 'A' ? 'High' : p === 'B' ? 'Med' : 'Low'}
                </span>
              </button>
            ))}
          </div>
        </Field>

        <Field
          label="First note"
          htmlFor="nl-first-note"
          full
          right={
            dictation.supported && (
              <button
                type="button"
                className={clsx('text-[12px]', dictation.listening ? 'text-red' : 'text-accent2')}
                onClick={() => (dictation.listening ? dictation.stop() : dictation.start())}
                aria-pressed={dictation.listening}
              >
                {dictation.listening ? '■ Stop' : '🎤 Dictate'}
              </button>
            )
          }
        >
          <textarea
            id="nl-first-note"
            rows={3}
            value={form.firstNote}
            onChange={(e) => set('firstNote', e.target.value)}
            placeholder={
              dictation.listening ? 'Listening…' : 'How did you meet? What do they need?'
            }
            className={clsx(dictation.listening && 'ring-2 ring-red/60')}
          />
          {dictation.interim && (
            <div className="text-xs text-text3 truncate mt-1">{dictation.interim}</div>
          )}
          {dictation.error && <div className="text-xs text-red mt-1">{dictation.error}</div>}
        </Field>

        <Field label="Next step" full htmlFor="nl-next">
          <input
            id="nl-next"
            value={form.nextAction}
            onChange={(e) => set('nextAction', e.target.value)}
            placeholder="e.g. Send intro deck"
            enterKeyHint="done"
            autoCapitalize="sentences"
          />
          <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap items-center">
            {NEXT_STEP_CHIPS.map((c) => {
              const d = addDays(c.days);
              const active = form.nextActionDate === d;
              return (
                <button
                  key={c.label}
                  type="button"
                  aria-pressed={active}
                  className={clsx(
                    'flex-shrink-0 rounded-full border px-3 min-h-[36px] text-[13px] font-medium',
                    active
                      ? 'bg-accent border-accent text-white'
                      : 'bg-surface2 border-border text-text2',
                  )}
                  onClick={() => set('nextActionDate', active ? '' : d)}
                >
                  {c.label}
                </button>
              );
            })}
            <input
              type="date"
              aria-label="Next step date"
              value={form.nextActionDate}
              onChange={(e) => set('nextActionDate', e.target.value)}
              className="!w-auto !min-h-[40px] !py-1 text-sm flex-shrink-0"
            />
          </div>
        </Field>

        {!showAll && (
          <div className="sm:col-span-2">
            <button
              type="button"
              className="btn btn-sm btn-ghost w-full"
              onClick={() => setShowAll(true)}
            >
              More fields ▾
            </button>
          </div>
        )}

        {showAll && (
          <>
            <Field label="Title">
              <input
                value={form.contactTitle}
                onChange={(e) => set('contactTitle', e.target.value)}
                enterKeyHint="next"
              />
            </Field>
            <Field label="Source">
              <input
                value={form.source}
                onChange={(e) => set('source', e.target.value)}
                placeholder="Warm intro, inbound, etc."
                enterKeyHint="next"
              />
            </Field>
            <Field label="NRE Value (₪)">
              <input
                type="number"
                inputMode="decimal"
                value={form.nreValue}
                onChange={(e) => set('nreValue', e.target.value)}
              />
            </Field>
            <Field label="Deal Value (₪/yr)">
              <input
                type="number"
                inputMode="decimal"
                value={form.dealValueYearly}
                onChange={(e) => set('dealValueYearly', e.target.value)}
              />
            </Field>
            <Field label="Owner">
              <select
                value={form.ownerUserId}
                onChange={(e) => set('ownerUserId', e.target.value)}
              >
                <option value="">Unassigned</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name ?? m.email}
                  </option>
                ))}
              </select>
            </Field>
            {cfs.map((f) => (
              <CustomFieldEditor
                key={f.id}
                field={f}
                value={form.customFields[f.id] ?? ''}
                onChange={(v) => set('customFields', { ...form.customFields, [f.id]: v })}
              />
            ))}
            {tags.length > 0 && (
              <Field label="Tags" full>
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => {
                    const on = form.tagIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        className="badge !py-1.5 !px-3 !text-xs"
                        type="button"
                        style={
                          on
                            ? { background: t.color, color: '#fff' }
                            : {
                                background: 'transparent',
                                color: t.color,
                                border: `1px solid ${t.color}55`,
                              }
                        }
                        onClick={() =>
                          set(
                            'tagIds',
                            on ? form.tagIds.filter((id) => id !== t.id) : [...form.tagIds, t.id],
                          )
                        }
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}
            <Field label="Notes" full>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            </Field>
          </>
        )}
      </div>
      {error && <div className="text-red text-sm mt-3">{error}</div>}
    </Modal>
  );
}

function Field({
  label,
  children,
  full,
  right,
  htmlFor,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
  right?: React.ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-[12px] text-text2 font-medium" htmlFor={htmlFor}>
          {label}
        </label>
        {right}
      </div>
      {children}
    </div>
  );
}

type CustomFieldValue = string | number | boolean | null;

function CustomFieldEditor({
  field,
  value,
  onChange,
}: {
  field: CustomField;
  value: CustomFieldValue;
  onChange: (v: CustomFieldValue) => void;
}) {
  if (field.type === 'select') {
    return (
      <Field label={field.label}>
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
    );
  }
  return (
    <Field label={field.label}>
      <input
        type={
          field.type === 'date'
            ? 'date'
            : field.type === 'number'
              ? 'number'
              : field.type === 'url'
                ? 'url'
                : field.type === 'email'
                  ? 'email'
                  : 'text'
        }
        inputMode={
          field.type === 'number'
            ? 'decimal'
            : field.type === 'url'
              ? 'url'
              : field.type === 'email'
                ? 'email'
                : undefined
        }
        autoCapitalize={field.type === 'url' || field.type === 'email' ? 'none' : undefined}
        value={value == null ? '' : String(value)}
        onChange={(e) =>
          onChange(
            field.type === 'number' && e.target.value !== ''
              ? Number(e.target.value)
              : e.target.value,
          )
        }
      />
    </Field>
  );
}
